// hook borrowed from dsch-donedeal-frontend
// components/Dashboard/pages/MyDoneDeal/hooks/useSaveChanges.ts

import { useEffect, useState, useCallback } from 'react';
import { useOnUpdateOnly } from '../useOnUpdateOnly/useOnUpdateOnly';
type useSaveChangesProps = {
  isDirty: boolean;
  submitSucceeded: boolean;
  hasSubmitErrors: boolean;
  hasValidationErrors: boolean;
  isSubmitting: boolean;
  handleRedirect: (url?: string) => void;
  // TODO come up with better prop names here for a more generic solution
  buttonName?: string;
  homepageUrl?: string;
  targetUrl?: string;
};

function useSaveChanges(props: useSaveChangesProps) {
  const {
    isDirty: isFormUpdated,
    submitSucceeded,
    hasSubmitErrors,
    isSubmitting,
    handleRedirect,
    buttonName = '',
    homepageUrl,
    targetUrl,
  } = props;
  const [saveChangesModalHref, setSaveChangesModalHref] = useState<string>();

  const handleModalActions = useCallback(
    (event: Event) => {
      event.preventDefault();
      const targetElement = event.target as
        | HTMLAnchorElement
        | HTMLButtonElement;
      // check to see if it's an anchor element to prevent TS giving out
      const href =
        targetElement instanceof HTMLAnchorElement
          ? targetElement.href
          : undefined;

      if (targetElement && targetElement.name === buttonName) {
        handleRedirect(href || targetUrl);
      } else if (!hasSubmitErrors) {
        const redirectUrl = href || homepageUrl;
        setSaveChangesModalHref(redirectUrl);
        handleRedirect(saveChangesModalHref || redirectUrl);
      }
    },
    [
      buttonName,
      hasSubmitErrors,
      handleRedirect,
      targetUrl,
      homepageUrl,
      saveChangesModalHref,
    ],
  );

  // Handle <a> tag click events
  useEffect(() => {
    const ANCHOR_TAGS = document.getElementsByTagName('a');
    if (document) {
      if (isFormUpdated) {
        Array.from(ANCHOR_TAGS).forEach((anchor) => {
          anchor.addEventListener('click', handleModalActions, false);
        });
      }
    }

    return () => {
      Array.from(ANCHOR_TAGS).forEach((anchor) => {
        anchor.removeEventListener('click', handleModalActions, false);
      });
    };
  }, [isFormUpdated, handleModalActions]);

  const alertUserBeforeBrowserNavigation = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = '';
  };

  useOnUpdateOnly(() => {
    if (isFormUpdated && !isSubmitting) {
      window.addEventListener(
        'beforeunload',
        alertUserBeforeBrowserNavigation,
        false,
      );
    } else {
      window.removeEventListener(
        'beforeunload',
        alertUserBeforeBrowserNavigation,
        false,
      );
    }
    return () => {
      window.removeEventListener(
        'beforeunload',
        alertUserBeforeBrowserNavigation,
        false,
      );
    };
  }, [isFormUpdated, submitSucceeded, isSubmitting]);
  // handle button events for place ad use case in edit ad form - we don't want it to trigger on all buttons
  useEffect(() => {
    const BUTTON_TAGS = document.getElementsByTagName('button');
    const LINK_TAGS = document.querySelectorAll(
      'a[data-save-changes], a[data-save-changes]',
    );

    if (isFormUpdated) {
      Array.from(BUTTON_TAGS).forEach((button) => {
        if (button.name === buttonName) {
          button.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              setSaveChangesModalHref(targetUrl);
              handleModalActions(event);
            },
            false,
          );
        }
      });
    }
    LINK_TAGS.forEach((link) => {
      link.addEventListener('click', handleModalActions, false);
    });

    return () => {
      Array.from(BUTTON_TAGS).forEach((button) => {
        if (button.name === buttonName) {
          button.removeEventListener('click', handleModalActions, false);
        }
      });
      LINK_TAGS.forEach((link) => {
        link.removeEventListener('click', handleModalActions, false);
      });
    };
  }, [isFormUpdated, handleModalActions, buttonName, targetUrl]);

  return {
    saveChangesModalHref,
  };
}

export { useSaveChanges };
